/**
 * ContactFeedbackModal — per-contact prompt overrides.
 * Feedbacks are stored on the contact and injected into the agent's system
 * prompt as MAX-PRIORITY rules for that specific conversation, on both
 * WA Business and WA Personal channels.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, Trash2, MessageSquarePlus, AlertCircle } from 'lucide-react';
import contactFeedbackService from '../../services/global/contactFeedbackService';

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ContactFeedbackModal = ({
  isOpen,
  onClose,
  contactPhone,
  contactName,
  colors,
  onSaved,
  // 'modal' (default, caixa centralizada) ou 'drawer' (painel lateral direito
  // — usado nos ChatWindows para padronizar com o ContactDetailsDrawer).
  displayMode = 'modal',
}) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadFeedbacks = useCallback(async () => {
    if (!contactPhone) return;
    setLoading(true);
    setError(null);
    try {
      const list = await contactFeedbackService.listFeedbacks(contactPhone);
      setFeedbacks(list);
    } catch (err) {
      console.error('[ContactFeedbackModal] load error:', err);
      setError('Não foi possível carregar os comentários.');
    } finally {
      setLoading(false);
    }
  }, [contactPhone]);

  useEffect(() => {
    if (isOpen) {
      loadFeedbacks();
      setText('');
      setError(null);
    }
  }, [isOpen, loadFeedbacks]);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      const newFeedback = await contactFeedbackService.addFeedback(
        contactPhone,
        trimmed
      );
      setFeedbacks((prev) => [...prev, newFeedback]);
      setText('');
      onSaved?.(newFeedback);
    } catch (err) {
      console.error('[ContactFeedbackModal] save error:', err);
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (feedbackId) => {
    setError(null);
    try {
      await contactFeedbackService.removeFeedback(contactPhone, feedbackId);
      setFeedbacks((prev) => prev.filter((f) => f.id !== feedbackId));
    } catch (err) {
      console.error('[ContactFeedbackModal] remove error:', err);
      setError('Não foi possível remover.');
    }
  };

  if (!isOpen) return null;

  const displayName = contactName || contactPhone || 'Contato';
  const isDrawer = displayMode === 'drawer';

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: isDrawer ? 'stretch' : 'center',
        justifyContent: isDrawer ? 'flex-end' : 'center',
        zIndex: 1000, padding: isDrawer ? 0 : 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        style={{
          width: isDrawer ? 420 : '100%',
          maxWidth: isDrawer ? '100%' : 560,
          height: isDrawer ? '100%' : 'auto',
          maxHeight: isDrawer ? '100%' : '85vh',
          backgroundColor: colors.bgPrimary,
          borderRadius: isDrawer ? 0 : 12,
          border: isDrawer ? 'none' : `1px solid ${colors.borderColor}`,
          boxShadow: isDrawer
            ? '-8px 0 32px rgba(0,0,0,0.25)'
            : '0 20px 50px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: 10,
                backgroundColor: `${colors.accentPrimary}1f`,
                color: colors.accentPrimary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <MessageSquarePlus size={18} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                Comentários para {displayName}
              </div>
              <div style={{ fontSize: 12, color: colors.textSecondary }}>
                {feedbacks.length} comentário{feedbacks.length === 1 ? '' : 's'} ativo{feedbacks.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 6, color: colors.textTertiary, borderRadius: 6,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Helper text */}
        <div
          style={{
            padding: '12px 20px',
            backgroundColor: colors.bgSecondary,
            color: colors.textSecondary,
            fontSize: 12,
            lineHeight: 1.5,
            display: 'flex', gap: 8, alignItems: 'flex-start',
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            Lembretes da equipe compartilhados com o agente para este contato.
            Eles ajustam o tom e o contexto da conversa sem substituir o
            treinamento.
          </span>
        </div>

        {/* Feedback list */}
        <div
          style={{
            flex: 1, overflowY: 'auto', padding: '12px 20px',
            display: 'flex', flexDirection: 'column', gap: 10,
            minHeight: 80,
          }}
        >
          {loading ? (
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: colors.textSecondary, padding: 20, gap: 8,
              }}
            >
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Carregando…
            </div>
          ) : feedbacks.length === 0 ? (
            <div
              style={{
                color: colors.textSecondary, fontSize: 13, fontStyle: 'italic',
                textAlign: 'center', padding: 16,
              }}
            >
              Nenhum comentário cadastrado ainda.
            </div>
          ) : (
            feedbacks.map((fb) => (
              <div
                key={fb.id}
                style={{
                  padding: 12,
                  backgroundColor: colors.bgSecondary,
                  borderRadius: 8,
                  border: `1px solid ${colors.borderColor}`,
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13, color: colors.textPrimary,
                      lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}
                  >
                    {fb.text}
                  </div>
                  <div
                    style={{
                      fontSize: 11, color: colors.textTertiary,
                      marginTop: 6,
                    }}
                  >
                    {formatTimestamp(fb.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(fb.id)}
                  title="Remover comentário"
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#DC2626', padding: 4, borderRadius: 6, flexShrink: 0,
                  }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Composer */}
        <div
          style={{
            padding: 16, borderTop: `1px solid ${colors.borderColor}`,
            backgroundColor: colors.bgSecondary,
          }}
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ex.: A partir de agora responda só de forma curta — esse cliente prefere objetividade."
            rows={3}
            disabled={saving}
            style={{
              width: '100%', padding: 10, fontSize: 13,
              backgroundColor: colors.bgPrimary,
              color: colors.textPrimary,
              border: `1px solid ${colors.borderColor}`,
              borderRadius: 8, resize: 'vertical', fontFamily: 'inherit',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          {error && (
            <div style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: colors.textSecondary,
                border: `1px solid ${colors.borderColor}`,
                borderRadius: 8, cursor: 'pointer', fontSize: 13,
              }}
            >
              Fechar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !text.trim()}
              style={{
                padding: '8px 18px',
                backgroundColor: colors.accentPrimary,
                color: '#fff',
                border: 'none', borderRadius: 8,
                cursor: !text.trim() || saving ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
                opacity: !text.trim() || saving ? 0.5 : 1,
              }}
            >
              {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              Salvar comentário
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFeedbackModal;
